import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { GraphAPI } from '@/api/graph'
import { DomainsAPI } from '@/api/domains'
import { HelpHint } from '@/components/HelpHint'
import { domainColorMap, domainStats } from '@/lib/graphColors'
import { compoundingSentence, crossDomainBridges, pairKey } from '@/lib/homeInsights'

// "Knowledge is compounding" — proven with the operator's OWN numbers, not a stock chart.
// The web below is their verse: one point per domain (sized by how many stars it holds),
// a faint dashed route for every domain pair the verse makes possible, and a solid line
// wherever real cross-domain relations already run. Same query keys as the Dashboard's
// stat tiles, so this costs no extra requests.

const GLOSS =
  'A bridge is a relation whose two ends live in different domains. Routes are the ' +
  'domain pairs your verse makes possible; bridges are the routes already carrying links.'

/** The domain web: points on a circle, dashed possible routes, solid live bridges. */
function Web({
  slugs,
  colors,
  counts,
  bridged,
}: {
  slugs: string[]
  colors: Map<string, string>
  counts: Map<string, number>
  bridged: Set<string>
}) {
  const W = 320
  const H = 150
  const cx = W / 2
  const cy = H / 2 + 4
  const r = 52
  const pos = slugs.map((slug, i) => {
    const a = -Math.PI / 2 + (2 * Math.PI * i) / slugs.length
    return { slug, x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }
  })
  const maxCount = Math.max(1, ...slugs.map((s) => counts.get(s) ?? 0))

  return (
    <svg className="dw-home-web" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Your domains and the links between them">
      {pos.map((a, i) =>
        pos.slice(i + 1).map((b) => {
          const live = bridged.has(pairKey(a.slug, b.slug))
          return (
            <line
              key={`${a.slug}|${b.slug}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              className={live ? 'dw-home-web-bridge' : 'dw-home-web-route'}
            >
              <title>{live ? `${a.slug} ↔ ${b.slug} — linked` : `${a.slug} ↔ ${b.slug} — no links yet`}</title>
            </line>
          )
        }),
      )}
      {pos.map((p) => {
        const count = counts.get(p.slug) ?? 0
        const dot = 3 + 3.5 * Math.sqrt(count / maxCount)
        const below = p.y >= cy
        return (
          <g key={p.slug}>
            <circle cx={p.x} cy={p.y} r={dot} fill={colors.get(p.slug) ?? 'var(--ae-color-text-muted)'} opacity={0.9}>
              <title>{`${p.slug} — ${count} ${count === 1 ? 'star' : 'stars'}`}</title>
            </circle>
            <text
              className="dw-home-web-label"
              x={p.x}
              y={below ? p.y + dot + 11 : p.y - dot - 5}
              textAnchor="middle"
            >
              {p.slug.length > 12 ? `${p.slug.slice(0, 11)}…` : p.slug}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function Compounding() {
  const { diwaUrl } = useUI()
  // Same keys as the Dashboard's tiles — served from the react-query cache, not refetched.
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => new DomainsAPI(diwaUrl).list() })
  const { data: graph } = useQuery({ queryKey: ['graph-network', 'all'], queryFn: () => new GraphAPI(diwaUrl).network('all') })

  if (!domainsData || !graph) return null

  const live = domainsData.domains.filter((d) => d.status !== 'archived')
  const slugs = live.map((d) => d.slug)
  const bridges = crossDomainBridges(graph.nodes, graph.edges)
  const stats = domainStats(graph.nodes)
  const counts = new Map(slugs.map((s) => [s, stats.get(s)?.count ?? 0]))
  const colors = domainColorMap(new Set([...slugs, ...graph.nodes.map((n) => n.domain)]))
  const sentence = compoundingSentence(slugs.length, bridges)

  return (
    <Card>
      <div className="dw-compounding-head">
        <div className="dw-rail-section-title" style={{ marginBottom: 0 }}>
          Compounding <HelpHint gloss={GLOSS} />
        </div>
        <div className="dw-compounding-totals">
          {slugs.length} {slugs.length === 1 ? 'domain' : 'domains'}
          <span className="dw-compounding-sep"> · </span>
          {graph.nodes.length} {graph.nodes.length === 1 ? 'star' : 'stars'}
          <span className="dw-compounding-sep"> · </span>
          {bridges.count} cross-domain {bridges.count === 1 ? 'link' : 'links'}
        </div>
      </div>

      {slugs.length >= 2 && <Web slugs={slugs} colors={colors} counts={counts} bridged={new Set(bridges.pairs)} />}

      <div className="dw-home-caption">
        Every domain you add multiplies the paths between what you track.
        <span className="dw-home-caption-fact"> {sentence}</span>
        {slugs.length === 0 && (
          <>
            {' '}
            <Link className="dw-link" to="/domains?create=1">
              Create your first domain →
            </Link>
          </>
        )}
      </div>
    </Card>
  )
}
